import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Le moutarde est réservé à l'action principale : un écran ne doit en porter
 * qu'un seul. Le texte sur moutarde reste noir, pour le contraste.
 */
const variantes = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ' +
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mustard-500 ' +
    'focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-mustard-500 text-ink hover:bg-mustard-600 hover:text-ink',
        secondary: 'bg-ink text-white hover:bg-ink-soft',
        outline: 'border border-line bg-white text-ink hover:bg-surface-sunken',
        ghost: 'text-ink hover:bg-surface-sunken',
        danger: 'bg-danger text-white hover:bg-danger/90',
        link: 'text-mustard-600 underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4',
        lg: 'h-11 px-6 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'outline', size: 'md' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof variantes> & {
    /** Affiche un indicateur et neutralise le bouton pendant l'action. */
    chargement?: boolean;
    icone?: ReactNode;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, chargement, icone, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || chargement}
      className={cn(variantes({ variant, size }), className)}
      {...props}
    >
      {chargement ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : icone}
      {children}
    </button>
  );
});

export { variantes as variantesBouton };
